import { c as _c } from "react/compiler-runtime";
import type { DetailedHTMLProps, HTMLAttributes } from 'react';
import Image from 'next/image';
import { FleetCategoryLink } from '@/components/FleetCategory/FleetCategoryLink';
import { FleetCategoryStarRating } from '@/components/FleetCategory/FleetCategoryStarRating';
import { Typography } from '@/components/Typography';
import { ToggleCompare } from '@/components/blocks/cms/VehicleCompareBlock/ToggleCompare';
import ModalSearchFormBtn from '@/components/forms/ModalSearchFormBtn';
import { breakpoints } from '@/lib/breakpoints';
import { cn } from '@/lib/cn';
import { formatCurrency } from '@/lib/formatCurrency';
import type { CmsFleetCategory } from '@/services/cms-sdk/';
import type { CmsFleetCategoryDataTag } from '@/services/cms-sdk/schemas/models/CmsFleetCategoryDataTagSchema';
export type CompareTabKey = 'specs' | 'features' | 'dimensions';
export const defaultCompareOpenTabs: CompareTabKey[] = ['specs'];
export interface KeyValue {
  key: string;
  value: string;
}
interface CompareTabProps extends DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  heading: string;
  showKey?: boolean;
  specs?: KeyValue[];
  defaultOpen?: boolean;
}
function CompareTab(t0) {
  const $ = _c(12);
  let defaultOpen;
  let heading;
  let props;
  let specs;
  let t1;
  if ($[0] !== t0) {
    ({
      heading,
      showKey: t1,
      specs,
      defaultOpen,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = defaultOpen;
    $[2] = heading;
    $[3] = props;
    $[4] = specs;
    $[5] = t1;
  } else {
    defaultOpen = $[1];
    heading = $[2];
    props = $[3];
    specs = $[4];
    t1 = $[5];
  }
  const showKey = t1 === undefined ? true : t1;
  let t2;
  if ($[6] !== defaultOpen || $[7] !== heading || $[8] !== props || $[9] !== showKey || $[10] !== specs) {
    t2 = specs?.length ? <div className="collapse collapse-arrow join-item border-b-base-300 border-b rounded-none group" {...props}><input type="checkbox" name={heading} defaultChecked={defaultOpen} /><Typography className="collapse-title pl-0 after:!end-[1rem] max-w-none" variant="h6">{heading}</Typography><div className="collapse-content px-0"><div className="collapse-equal-height min-h-(--equal-height) flex flex-col gap-3 md:gap-4">{specs?.map((t3, index) => {
            const {
              key,
              value
            } = t3;
            return <div key={`${key}-${value}-${index}`} className="flex gap-1">{showKey && <Typography variant="paragraph-heavy">{key}:</Typography>}<Typography variant="paragraph">{value}</Typography></div>;
          })}</div></div></div> : null;
    $[6] = defaultOpen;
    $[7] = heading;
    $[8] = props;
    $[9] = showKey;
    $[10] = specs;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
}
export interface TagProps {
  tag: CmsFleetCategoryDataTag | undefined;
}
export interface FleetCategoryComparisonCardProps {
  fleetCategory: CmsFleetCategory;
  showToggleCompare?: boolean;
  className?: string;
}
export function FleetCategoryComparisonCard(t0) {
  const $ = _c(55);
  const {
    className,
    fleetCategory,
    showToggleCompare: t1
  } = t0;
  const showToggleCompare = t1 === undefined ? true : t1;
  let t2;
  if ($[0] !== fleetCategory.specifications) {
    t2 = fleetCategory.specifications ? Object.entries(fleetCategory.specifications).reduce(_temp, [[] as KeyValue[], [] as KeyValue[]]) : [[] as KeyValue[], [] as KeyValue[]];
    $[0] = fleetCategory.specifications;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [specs_0, dimensions] = t2;
  let t3;
  if ($[2] !== fleetCategory.data.features) {
    t3 = fleetCategory.data.features ? Object.entries(fleetCategory.data.features).map(_temp2) : [];
    $[2] = fleetCategory.data.features;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const features = t3;
  let t4;
  if ($[4] !== className) {
    t4 = cn("flex flex-col gap-6 h-full", className);
    $[4] = className;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== fleetCategory || $[7] !== showToggleCompare) {
    t5 = showToggleCompare ? <div className="absolute top-0 right-0 z-1"><ToggleCompare className="border-primary text-primary size-9.5" fleetCategory={fleetCategory} variant="icon" /></div> : null;
    $[6] = fleetCategory;
    $[7] = showToggleCompare;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== fleetCategory) {
    t6 = fleetCategory.studioImage ? <FleetCategoryLink className="relative w-54 md:w-78" noUrlComponent="div" fleetCategory={fleetCategory}><Image src={fleetCategory.studioImage} alt={fleetCategory.name} fill={true} sizes={`(max-width: ${breakpoints.sm}) 216px, 312px`} className="object-contain" /></FleetCategoryLink> : fleetCategory.scenicImage ? <FleetCategoryLink fleetCategory={fleetCategory}><Image src={fleetCategory.scenicImage} alt={fleetCategory.name} width={488} height={300} className="aspect-488/300 object-contain" /></FleetCategoryLink> : null;
    $[9] = fleetCategory;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t5 || $[12] !== t6) {
    t7 = <div className="relative flex flex-row justify-center h-38 md:h-56">{t5}{t6}</div>;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== fleetCategory.rating) {
    t8 = <div className="flex flex-row gap-2 justify-center items-center h-5"><FleetCategoryStarRating rating={fleetCategory.rating} /></div>;
    $[14] = fleetCategory.rating;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== fleetCategory.name) {
    t9 = <Typography as="h3" variant="h3" className="line-clamp-1 text-center text-[24px] md:text-[32px]">{fleetCategory.name}</Typography>;
    $[16] = fleetCategory.name;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== fleetCategory || $[19] !== t9) {
    t10 = <FleetCategoryLink fleetCategory={fleetCategory}>{t9}</FleetCategoryLink>;
    $[18] = fleetCategory;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== fleetCategory.estimatedDailyRate) {
    t11 = fleetCategory.estimatedDailyRate ? <Typography variant="paragraph" className="text-[18px] md:text-[20px] text-center">From {formatCurrency(fleetCategory.estimatedDailyRate, {
        minimumFractionDigits: 2
      })}/day</Typography> : null;
    $[21] = fleetCategory.estimatedDailyRate;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t10 || $[24] !== t11) {
    t12 = <div className="flex flex-col gap-2">{t10}{t11}</div>;
    $[23] = t10;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t12 || $[27] !== t8) {
    t13 = <div className="flex flex-col gap-3">{t8}{t12}</div>;
    $[26] = t12;
    $[27] = t8;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== specs_0) {
    t14 = <CompareTab heading="Vehicle specs" data-group-name="specs" specs={specs_0} defaultOpen={true} />;
    $[29] = specs_0;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== features) {
    t15 = <CompareTab heading="Features" data-group-name="features" specs={features} />;
    $[31] = features;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== dimensions) {
    t16 = <CompareTab heading="Dimensions" data-group-name="dimensions" specs={dimensions} />;
    $[33] = dimensions;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t14 || $[36] !== t15 || $[37] !== t16) {
    t17 = <div className="flex flex-col border-y-1 border-y-base-300 grow justify-between">{t14}{t15}{t16}</div>;
    $[35] = t14;
    $[36] = t15;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== fleetCategory) {
    t18 = fleetCategory.pages ? <FleetCategoryLink className="btn bg-white border-primary text-primary h-13 text-nowrap" fleetCategory={fleetCategory}>Learn more</FleetCategoryLink> : <div />;
    $[39] = fleetCategory;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== fleetCategory.code || $[42] !== fleetCategory.fleetTypeSlug) {
    t19 = <ModalSearchFormBtn className="btn btn-primary h-13 text-nowrap w-full" values={{
      fleetType: fleetCategory.fleetTypeSlug,
      fleetCategoryCode: fleetCategory.code
    }} openStep="locations">Book now</ModalSearchFormBtn>;
    $[41] = fleetCategory.code;
    $[42] = fleetCategory.fleetTypeSlug;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== t18 || $[45] !== t19) {
    t20 = <div className="grid grid-cols-2 gap-4 items-center">{t18}{t19}</div>;
    $[44] = t18;
    $[45] = t19;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] !== t13 || $[48] !== t17 || $[49] !== t20) {
    t21 = <div className="flex flex-col gap-4 md:gap-6 grow">{t13}{t17}{t20}</div>;
    $[47] = t13;
    $[48] = t17;
    $[49] = t20;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== t21 || $[52] !== t4 || $[53] !== t7) {
    t22 = <div className={t4}>{t7}{t21}</div>;
    $[51] = t21;
    $[52] = t4;
    $[53] = t7;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  return t22;
}
function _temp2(t0) {
  const [key_0, value_0] = t0;
  return {
    key: key_0,
    value: value_0
  };
}
function _temp(t0, t1) {
  const [specs, dim] = t0;
  const [key, value] = t1;
  if (["Height", "Interior height", "Interior width", "Interior length", "Length", "Width"].includes(key)) {
    return [specs, [...dim, {
      key,
      value
    }]];
  }
  return [[...specs, {
    key,
    value
  }], dim];
}